import * as THREE from "three";
import { PhysicsBreakSpeed, PhysicsFriction, PhysicsMass, PhysicsRestitution } from "../physics/physics_masses.js";
import { edgeSupportFor } from "../physics/edge_support_pad.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { PhysicsCube } from "./physics_cube.js";
import { PhysicsTable } from "./physics_table.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { hapticsController, VibrationContext } from "../haptics/haptics_controller.js";
import { HapticPattern } from "../haptics/haptics_constants.js";
import { EntityKind, LevelEntity } from "../types/level_entity.js";
import { BreakablePhysicsEntity } from "./breakable_physics_entity.js";
import { BlockImpactMaterial } from "../vfx_3d/effects/ball_impact/vfx_ball_block_impact_task.js";

export class IceBlock extends BreakablePhysicsEntity implements LevelEntity
{
  public static readonly MIN_IMPACT_SPEED = PhysicsBreakSpeed.ice_block;
  public static readonly SHADOW_FILL_COLOR = new THREE.Color( 0x79cfff );
  public static readonly SHADOW_FILL_INTENSITY = 0.32;

  private static readonly MODELS: Record<number, ResourceID> = {
    1: ResourceID.IceModel,
    2: ResourceID.Block1x2Model,
    3: ResourceID.Block1x3Model,
  };

  private static readonly TEXTURES: Record<number, ResourceID> = {
    1: ResourceID.IceSmallTexture,
    2: ResourceID.IceBigTexture,
    3: ResourceID.IceGiantTexture,
  };

  // A block occupies exactly one of these two axes (1-3), never both — the
  // model only exists as a vertical column, so a "wide" piece is the same
  // model/texture rotated -90° onto its side (see createMesh). Matches
  // PhysicsCube.createAuthoredMesh's `width > height` rotation convention.
  public readonly width_blocks: number;
  public readonly height_blocks: number;
  private static readonly material_cache = new Map<number, THREE.MeshPhongMaterial>();

  public readonly kind = EntityKind.Ice;

  private outside_pending = false;
  private outside_timer = 0;
  private is_cracked = false;
  private static shared_cracked_material?: THREE.MeshPhongMaterial;

  constructor(
    position: THREE.Vector3,
    width_blocks = 1,
    height_blocks = 1,
    lying = false,
    rotation?: { x?: number; y?: number; z?: number },
    surface_tilt_rad = 0
  )
  {
    const blocks = THREE.MathUtils.clamp( Math.round( Math.max( width_blocks, height_blocks ) ), 1, 3 );
    const horizontal = width_blocks > height_blocks;
    const mesh = IceBlock.createMesh( blocks, horizontal, lying );

    super( mesh, position, {
      angular_damping: 0.35,
      extra_colliders: edgeSupportFor( mesh, rotation, surface_tilt_rad ),
      friction: PhysicsFriction.ice,
      initial_rotation_x: rotation?.x,
      initial_rotation_y: rotation?.y,
      initial_rotation_z: rotation?.z,
      linear_damping: 0.04,
      mass: PhysicsMass.ice_block_per_cell * blocks,
      restitution: PhysicsRestitution.ice,
      arm_delay: 0.2,
    } );

    this.width_blocks = horizontal ? blocks : 1;
    this.height_blocks = horizontal ? 1 : blocks;
  }

  private static createMesh( blocks: number, horizontal: boolean, lying: boolean ): THREE.Object3D
  {
    const model_id = IceBlock.MODELS[blocks];
    const visual = ResourceManager.getModel( model_id );
    const material = IceBlock.getMaterial( blocks );

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) ) return;
      child.castShadow = true;
      child.receiveShadow = blocks === 1;
      child.material = material;
    } );

    // The model is authored standing up along local Y — lay it on its side
    // for a horizontal (width > height) piece, same convention as
    // PhysicsCube.createAuthoredMesh's authored long models.
    if ( horizontal )
      visual.rotation.z = -Math.PI * 0.5;

    visual.updateMatrixWorld( true );
    const base_size = new THREE.Box3().setFromObject( visual ).getSize( new THREE.Vector3() );
    if ( base_size.x <= 0 || base_size.y <= 0 || base_size.z <= 0 )
      throw new Error( `Ice block model ${model_id} has invalid dimensions` );

    const target_size = PhysicsCube.getSize();
    if ( horizontal )
    {
      visual.scale.set(
        target_size.x * blocks / base_size.x,
        target_size.y / base_size.y,
        target_size.z / base_size.z
      );
    }
    else
    {
      visual.scale.set(
        target_size.x / base_size.x,
        target_size.y * blocks / base_size.y,
        target_size.z / base_size.z
      );
    }

    visual.updateMatrixWorld( true );
    const center = new THREE.Box3().setFromObject( visual ).getCenter( new THREE.Vector3() );
    visual.position.sub( center );

    const group = new THREE.Group();
    group.add( visual );

    if ( lying ) group.rotation.x = Math.PI / 2;

    const root = new THREE.Group();
    root.add( group );
    return root;
  }

  private static getMaterial( height_blocks: number ): THREE.MeshPhongMaterial
  {
    const cached = this.material_cache.get( height_blocks );
    if ( cached ) return cached;

    const map = ResourceManager.getTexture( this.TEXTURES[height_blocks] );
    const material = new THREE.MeshPhongMaterial( {
      map,
      normalMap: ResourceManager.getNormalMap( ResourceID.IceNormalTexture ),
      normalScale: new THREE.Vector2( 0.45, 0.45 ),
      shininess: 90,
      specular: new THREE.Color( 0xcfe8ff ),
      emissive: IceBlock.SHADOW_FILL_COLOR,
      emissiveMap: map,
      emissiveIntensity: IceBlock.SHADOW_FILL_INTENSITY,
    } );

    this.material_cache.set( height_blocks, material );
    return material;
  }

  public static crackedMaterial(): THREE.MeshPhongMaterial | null
  {
    if ( this.shared_cracked_material )
      return this.shared_cracked_material;

    const texture = ResourceManager.getTexture( ResourceID.IceCrackedTexture );
    if ( !texture )
      return null;

    const cracked_map = texture.clone();
    cracked_map.needsUpdate = true;

    this.shared_cracked_material = new THREE.MeshPhongMaterial( {
      map: cracked_map,
      normalMap: ResourceManager.getNormalMap( ResourceID.IceCrackedNormalTexture ),
      normalScale: new THREE.Vector2( 0.45, 0.45 ),
      shininess: 90,
      specular: new THREE.Color( 0xcfe8ff ),
      emissive: IceBlock.SHADOW_FILL_COLOR,
      emissiveMap: cracked_map,
      emissiveIntensity: IceBlock.SHADOW_FILL_INTENSITY,
    } );
    return this.shared_cracked_material;
  }

  public static createWarmupProbe(): THREE.Mesh
  {
    const material = this.crackedMaterial() ?? new THREE.MeshPhongMaterial();
    const mesh = new THREE.Mesh( new THREE.BoxGeometry( 0.01, 0.01, 0.01 ), material );
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.renderOrder = 10;
    return mesh;
  }

  public checkDestroy( dt: number, table: PhysicsTable ): void
  {
    if ( this.destroying ) return;
    const pos = this.body.translation();
    const is_outside = table.isOutside( pos );
    const bottom_y = pos.y - this.halfHeight();

    if ( !is_outside )
    {
      this.outside_pending = false;
    }
    else if ( is_outside && bottom_y < 0.2 )
    {
      const linvel = this.body.linvel();
      const impact_speed = Math.sqrt( linvel.x * linvel.x + linvel.y * linvel.y + linvel.z * linvel.z );
      this.destroy( 0.0, impact_speed );
      return;
    }

    if ( is_outside && !this.outside_pending )
    {
      this.outside_pending = true;
      this.outside_timer = 2;
    }

    if ( this.outside_pending && ( this.outside_timer -= dt ) <= 0 )
    {
      const linvel = this.body.linvel();
      const impact_speed = Math.sqrt( linvel.x * linvel.x + linvel.y * linvel.y + linvel.z * linvel.z );
      this.destroy( 0.0, impact_speed );
    }
  }

  public isAtRest(): boolean
  {
    return !this.outside_pending && super.isAtRest();
  }

  public playHitSound(): void
  {
    void audioController.playSound( AudioClips.ICE_HIT );
  }

  public static applyCrackedTextureUV( texture: THREE.Texture, height_blocks: number, flip_y = true ): void
  {
    if ( height_blocks === 1 )
    {
      texture.offset.set( 0.0, flip_y ? 2 / 3 : 0.0 );
      texture.repeat.set( 1 / 3, 1 / 3 );
    }
    else if ( height_blocks === 2 )
    {
      texture.offset.set( 0.0, flip_y ? 0.0 : 1 / 3 );
      texture.repeat.set( 1 / 3, 2 / 3 );
    }
    else if ( height_blocks === 3 )
    {
      texture.offset.set( 1 / 3, 0.0 );
      texture.repeat.set( 1 / 3, 1.0 );
    }
  }

  public isCracked(): boolean
  {
    return this.is_cracked;
  }

  public crack( hit_point?: THREE.Vector3 ): void
  {
    if ( this.is_cracked || this.destroying ) return;

    this.is_cracked = true;
    this.resetArmDelay();

    const crack_pos = hit_point ? hit_point.clone() : this.getPosition();
    Vfx3DManager.addIceCrack( crack_pos );

    const material = IceBlock.crackedMaterial();
    if ( material )
    {
      this.mesh.traverse( child =>
      {
        if ( child instanceof THREE.Mesh )
        {
          child.material = material;
        }
      } );
    }

    this.playHitSound();
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  }

  public getPosition(): THREE.Vector3
  {
    const pos = this.body.translation();
    return new THREE.Vector3( pos.x, pos.y, pos.z );
  }

  public canBeHit(): boolean
  {
    return this.isAlive() && this.isArmed();
  }

  public onBallHit( point: THREE.Vector3 ): void
  {
    Vfx3DManager.addCannonBallBlockImpact( point, BlockImpactMaterial.ICE );
    if ( this.isCracked() )
      this.shatter();
    else
      this.crack( point );
  }

  public onFireworkHit( point: THREE.Vector3 ): void
  {
    this.shatter();
  }

  public shatter(): void
  {
    this.playHitSound();

    const raw_speed = this.getImpactSpeed();
    const impact_speed = Math.min( 4.5, Math.max( 2.5, raw_speed * 0.3 ) );

    this.destroy( 0.0, impact_speed );

    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  }

  public checkSelfDestruct(): void
  {
    if ( !this.isAlive() )
      return;

    const bottom_y = this.getPosition().y - this.halfHeight();

    if ( bottom_y < 0.2 )
    {
      this.shatter();
      return;
    }

    if ( this.isArmed() && this.getImpactSpeed() >= IceBlock.MIN_IMPACT_SPEED )
    {
      if ( this.isCracked() )
        this.shatter();
      else
        this.crack();
    }
  }

  public destroy( floor_y: number = 0.0, impact_speed: number = 0.0 ): void
  {
    if ( !this.markDestroyed() ) return;

    this.spawnShatterVfx( floor_y, impact_speed );
  }

  private spawnShatterVfx( floor_y: number, impact_speed: number ): void
  {
    const blocks = Math.max( this.width_blocks, this.height_blocks );
    const target_size = PhysicsCube.getSize();
    const bounds_size = new THREE.Vector3(
      target_size.x * this.width_blocks,
      target_size.y * this.height_blocks,
      target_size.z
    );

    Vfx3DManager.addIceBreak( this.mesh.position, bounds_size );

    Vfx3DManager.addBlockShatter(
      this.mesh.position,
      this.mesh.quaternion,
      PhysicsCube.DEFAULT_SCALE,
      ResourceID.IceCrackedTexture,
      6 * blocks,
      floor_y,
      impact_speed,
      new THREE.Vector2( this.width_blocks, this.height_blocks )
    );
  }

  public dispose(): void
  {
    super.dispose();
  }
}
